import type { AnalysisSegmentInput } from '@/shared/hooks/queries/generated';

// ============================================================================
// Segment global d'une analyse — sous-filtre ÉPHÉMÈRE (état client) sur le
// snapshot figé, par ids Vinted (conditionIds/sizeIds/colorIds/prix), passé
// aux queries dashboard/niches/feed. La marque n'en fait pas partie
// (frontière de monétisation). Les options viennent de
// getAnalysisSegmentFacets ; les dimensions déjà contraintes par le filtre de
// création (Analysis.parsedFilter) sont verrouillées 🔒.
// ============================================================================

export type SegmentDimension = 'condition' | 'size' | 'color' | 'country' | 'price';

export interface AnalysisSegment {
  conditionIds: number[];
  sizeIds: number[];
  colorIds: number[];
  /** 2026-09-04 : pays du vendeur (snapshoté). NULL exclu dès que posé. */
  countryIds: number[];
  priceMin: number | null;
  priceMax: number | null;
}

export const EMPTY_SEGMENT: AnalysisSegment = {
  conditionIds: [],
  sizeIds: [],
  colorIds: [],
  countryIds: [],
  priceMin: null,
  priceMax: null,
};

export const isSegmentActive = (segment: AnalysisSegment): boolean =>
  segment.conditionIds.length > 0 ||
  segment.sizeIds.length > 0 ||
  segment.colorIds.length > 0 ||
  segment.countryIds.length > 0 ||
  segment.priceMin != null ||
  segment.priceMax != null;

/** Convertit l'état client en argument GraphQL (null si segment vide → cache serveur). */
export const toSegmentInput = (segment: AnalysisSegment): AnalysisSegmentInput | null => {
  if (!isSegmentActive(segment)) return null;
  return {
    conditionIds: segment.conditionIds.length ? segment.conditionIds : undefined,
    sizeIds: segment.sizeIds.length ? segment.sizeIds : undefined,
    colorIds: segment.colorIds.length ? segment.colorIds : undefined,
    // Champ inconnu du serveur pré-déploiement : n'est émis que si non vide
    countryIds: segment.countryIds.length ? segment.countryIds : undefined,
    priceMin: segment.priceMin ?? undefined,
    priceMax: segment.priceMax ?? undefined,
  };
};

// ─── Dimensions verrouillées par le filtre de création (parsedFilter) ──────

export interface LockedDimension {
  dimension: SegmentDimension;
  /** Valeurs affichables (ids bruts — résolus en titres via les facettes). */
  values: string[];
}

/**
 * `parsedFilter` est le filtre ids-only utilisé au backfill de création.
 * Une dimension déjà contrainte n'est plus un choix de segment : chip 🔒.
 */
export const getLockedDimensions = (parsedFilter: Record<string, unknown> | null | undefined): LockedDimension[] => {
  if (!parsedFilter) return [];
  const locked: LockedDimension[] = [];

  const idList = (key: string): number[] => {
    const value = parsedFilter[key];
    return Array.isArray(value) ? value.filter((v): v is number => typeof v === 'number') : [];
  };

  const conditionIds = idList('conditionIds');
  if (conditionIds.length) locked.push({ dimension: 'condition', values: conditionIds.map(String) });

  const sizeIds = idList('sizeIds');
  if (sizeIds.length) locked.push({ dimension: 'size', values: sizeIds.map(String) });

  const colorIds = idList('colorIds');
  if (colorIds.length) locked.push({ dimension: 'color', values: colorIds.map(String) });

  // 2026-09-02 : filtre pays posé à la création -> dimension verrouillée
  const countryIds = idList('countryIds');
  if (countryIds.length) locked.push({ dimension: 'country', values: countryIds.map(String) });

  const priceMin = typeof parsedFilter.priceMin === 'number' ? parsedFilter.priceMin : null;
  const priceMax = typeof parsedFilter.priceMax === 'number' ? parsedFilter.priceMax : null;
  if (priceMin != null || priceMax != null) {
    locked.push({ dimension: 'price', values: [priceMin != null ? String(priceMin) : '', priceMax != null ? String(priceMax) : ''] });
  }

  return locked;
};

export const isDimensionLocked = (locked: LockedDimension[], dimension: SegmentDimension): boolean =>
  locked.some((l) => l.dimension === dimension);
